import { addDays, differenceInCalendarDays, format, formatISO, getDay, parseISO, startOfDay, startOfWeek } from 'date-fns';
import {
  cadenceSchedule,
  confirmationStateOf,
  isInCadenceScope,
  needsDeposit,
  studioDayKey,
  type CadenceSchedule,
  type ConfirmationState,
} from '@atink/domain';
import type { Booking, ConfirmationCall, ConfirmationTouch, StudioId } from '@/domain/types';
import { confirmationDeadlineState } from '@/utils/confirmationTemplates';

// ---------------------------------------------------------------------------
// Confirmation cadence (vault System Diagram 06). Appointment days pair up -
// (Mon+Tue), (Wed+Thu), (Fri+Sat). Each booking gets three call sequences on
// three straight working days, worked from a Thursday ~10 days ahead, skipping
// Sundays; the ops decision falls the next working day. After the 3rd
// unanswered sequence the booking escalates to Ops (routed to `chase` here).
// Short-notice / gap-fill bookings skip the cadence (no room for it).
// ---------------------------------------------------------------------------

export type ConfirmationPair = 'mon-tue' | 'wed-thu' | 'fri-sat';

export interface ConfirmationSchedule {
  pair: ConfirmationPair;
  /** Start-of-day ISO anchors for the three sequence days + the ops decision day. */
  seq1Iso: string;
  seq2Iso: string;
  seq3Iso: string;
  decisionIso: string;
}

export type ConfirmationLane = 'seq-1' | 'seq-2' | 'seq-3' | 'two-day' | 'day-of';

const SEQ_LANES: readonly ConfirmationLane[] = ['seq-1', 'seq-2', 'seq-3'];
const PAIR_BY_INDEX: readonly ConfirmationPair[] = ['mon-tue', 'wed-thu', 'fri-sat'];

/** Which paired-day bucket (0..2) an appointment day-of-week falls into. */
function pairIndexForDow(dow: number): number {
  if (dow === 1 || dow === 2) return 0; // Mon/Tue
  if (dow === 3 || dow === 4) return 1; // Wed/Thu
  if (dow === 5 || dow === 6) return 2; // Fri/Sat
  return 0; // Sunday fallback - no Sunday appointments in the demo.
}

/**
 * Advance `n` calendar days from `d`; after each +1, if it lands on a Sunday
 * skip one more day. Saturday IS a working day - only Sundays are skipped.
 */
function addWorkingDays(d: Date, n: number): Date {
  let cur = d;
  for (let i = 0; i < n; i += 1) {
    cur = addDays(cur, 1);
    if (getDay(cur) === 0) cur = addDays(cur, 1);
  }
  return cur;
}

/**
 * The three sequence days + ops decision day for a booking's appointment,
 * derived purely from `booking.start`. See the module header for the model.
 */
export function confirmationSchedule(booking: Booking): ConfirmationSchedule {
  const start = startOfDay(new Date(booking.start));
  const pairIndex = pairIndexForDow(getDay(start));
  const weekMonday = startOfWeek(start, { weekStartsOn: 1 });
  const baseThursday = addDays(weekMonday, -11); // always a Thursday
  const seq1 = addWorkingDays(baseThursday, pairIndex);
  const seq2 = addWorkingDays(seq1, 1);
  const seq3 = addWorkingDays(seq2, 1);
  const decision = addWorkingDays(seq3, 1);
  return {
    pair: PAIR_BY_INDEX[pairIndex]!,
    seq1Iso: formatISO(seq1),
    seq2Iso: formatISO(seq2),
    seq3Iso: formatISO(seq3),
    decisionIso: formatISO(decision),
  };
}

/**
 * Minimum lead time (appointment date − booking-made date, in whole days) a
 * booking needs to run the pre-day confirmation cadence. Booked with less than
 * this it is genuinely last-minute, so there is no room for the ladder and it is
 * auto-confirmed at creation instead. Set to the same 7-day line as the
 * `refillable` deadline (confirmationDeadlineState): booking inside the final
 * week is last-minute.
 */
export const SHORT_NOTICE_LEAD_DAYS = 7;

/**
 * A short-notice / gap-fill booking: made with less than SHORT_NOTICE_LEAD_DAYS
 * of lead time before its appointment, so the pre-day cadence has no room to run
 * and it is auto-confirmed at booking.
 *
 * Keyed off LEAD TIME (appointment date vs when the booking was made), NOT the
 * appointment-anchored seq1 day. The old rule dropped any booking created on or
 * after its (fixed, ~11-days-before) first-call day — which wrongly excluded a
 * booking made with plenty of runway just because it landed after that theoretical
 * day (e.g. booked 13 days out for a Sunday appointment whose cadence anchors two
 * weeks earlier). Now such a booking still runs the ladder; only a genuinely
 * last-minute booking (inside SHORT_NOTICE_LEAD_DAYS) skips it.
 */
export function isShortNoticeBooking(booking: Booking): boolean {
  const leadDays = differenceInCalendarDays(new Date(booking.start), new Date(booking.createdAt));
  return leadDays < SHORT_NOTICE_LEAD_DAYS;
}

/**
 * Which confirmation lane a booking's work is due in on the workday `nowIso`,
 * or null. Pure + date-only. The appointment day itself is always `day-of`.
 * A confirmed booking only needs the 2-day reminder. Otherwise the next
 * unattempted sequence is due once its (roll-forward) sequence day is reached;
 * once all three are exhausted the booking has escalated to Ops (returned as
 * null here - it is no longer a call lane).
 */
export function confirmationWorkDue(booking: Booking, nowIso: string): ConfirmationLane | null {
  const now0 = startOfDay(new Date(nowIso)).getTime();
  const start = new Date(booking.start);
  const start0 = startOfDay(start).getTime();

  if (now0 === start0) return 'day-of';

  // A Sweeper gap-fill confirmed on the call bypasses the cadence entirely
  // (bd tv-pt8): it is never a call/text lane, only the day-of floor concern.
  if (booking.confirmedOnCall) return null;

  if (booking.confirmedAt) {
    // Confirmed bookings only need the 2-day reminder.
    if (now0 === startOfDay(addDays(start, -2)).getTime()) return 'two-day';
    return null;
  }

  if (isShortNoticeBooking(booking)) return null; // gap-fill / short-notice skip cadence

  const attempts = booking.confirmationAttempts ?? 0;
  if (attempts >= 3) return null; // cadence exhausted -> escalation, not a call lane

  const s = confirmationSchedule(booking);
  const dueSeqIso = [s.seq1Iso, s.seq2Iso, s.seq3Iso][attempts]!; // next sequence to attempt
  if (now0 >= startOfDay(new Date(dueSeqIso)).getTime()) return SEQ_LANES[attempts]!;
  return null;
}

/**
 * The booking has escalated to Ops: three confirmation sequences went unanswered
 * and it is still unconfirmed. This is the flag the Ops worklist consumes; it is
 * raised as soon as the 3rd sequence is unanswered (not gated on the decision
 * day, so an escalated booking never falls out of every lane for a day).
 */
export function isConfirmationEscalated(booking: Booking): boolean {
  return !booking.confirmedAt && (booking.confirmationAttempts ?? 0) >= 3;
}

/** One sequence touch on a row's timeline: sent (with its outcome), due today-or-rolled, or still to come. */
export interface TouchMark {
  seq: 1 | 2 | 3;
  /** Local day key (YYYY-MM-DD) the touch was sent, or is due. */
  day: string;
  state: 'sent' | 'due' | 'upcoming';
  /** Sent touches only: the recorded outcome. */
  outcome?: ConfirmationTouch['outcome'];
}

export interface ConfirmationRow {
  booking: Booking;
  /** The @atink/domain confirmation state this row was bucketed on. */
  state: ConfirmationState;
  /** The sequence lane due (seq rows only), else null. */
  lane: ConfirmationLane | null;
  /**
   * Informational: the booking is unconfirmed and inside the refillable /
   * deadline-approaching window per `confirmationDeadlineState`.
   */
  atRisk: boolean;
  /**
   * A sequence row waiting past its day (rolled forward, or its scheduled touch
   * went stale), or an escalated row. Never true for a confirmed row, and never
   * for a short-notice / gap-fill booking (it skips the cadence).
   */
  overdue: boolean;
  /** Call-confirmed but not solid (held / pending deposit): stays held, needs a deposit. */
  needsDeposit: boolean;
  /** Local day key (YYYY-MM-DD) of the appointment. */
  appointmentDay: string;
  /** The date of each sequence touch sent or due (empty for confirmed-at-booking). */
  touches: TouchMark[];
  call?: ConfirmationCall;
}

/** 7 day anchors (ISO, local offset preserved) starting from `nowIso`'s day. */
export function weekDayStrip(nowIso: string): string[] {
  const base = startOfDay(new Date(nowIso));
  return Array.from({ length: 7 }, (_, i) => formatISO(addDays(base, i)));
}

export type WorklistTab = 'first' | 'second' | 'third' | 'reds' | 'confirmed';

export interface ConfirmationWorklist {
  first: ConfirmationRow[];
  second: ConfirmationRow[];
  third: ConfirmationRow[];
  reds: ConfirmationRow[];
  confirmed: ConfirmationRow[];
  counts: Record<WorklistTab, number>;
}

/** Studio (Melbourne) day key, so the cut-offs match `cadenceSchedule` in any browser zone. */
const localKey = (iso: string) => studioDayKey(iso);

/** Touches recorded for THIS slot: a moved booking's pre-move touches are history. */
function touchesForSlot(b: Booking): ConfirmationTouch[] {
  const hist = b.rescheduleHistory ?? [];
  const since = hist.length ? Date.parse(hist[hist.length - 1]!.at) : null;
  return [...(b.confirmationTouches ?? [])]
    .filter((t) => since === null || Number.isNaN(since) || Date.parse(t.at) >= since)
    .sort((a, z) => a.at.localeCompare(z.at));
}

/**
 * The row's touch timeline: each of the three sequence touches with the date it
 * was sent (recorded touch, else its cadence day) or is due. A call-confirmed
 * booking ends at the confirming touch; confirmed-at-booking has no cadence.
 */
export function touchTimeline(b: Booking, state: ConfirmationState, nowIso: string): TouchMark[] {
  if (state === 'confirmed-at-booking' || state === 'not-in-cadence') return [];
  const s = cadenceSchedule(b.start);
  const days = [s.seq1, s.seq2, s.seq3];
  const recorded = touchesForSlot(b);
  const attempts = Math.min(3, Math.max(0, Math.floor(b.confirmationAttempts ?? 0)));
  const today = localKey(nowIso);
  const marks: TouchMark[] = [];
  for (let i = 0; i < 3; i += 1) {
    const seq = (i + 1) as 1 | 2 | 3;
    if (i < attempts) {
      const t = recorded[i];
      marks.push({ seq, day: t ? localKey(t.at) : days[i]!, state: 'sent', outcome: t?.outcome ?? 'no-response' });
      continue;
    }
    if (state === 'confirmed') {
      const t = [...recorded].reverse().find((r) => r.outcome === 'confirmed');
      const at = t?.at ?? b.confirmedAt;
      marks.push({ seq, day: at ? localKey(at) : days[i]!, state: 'sent', outcome: 'confirmed' });
      break;
    }
    marks.push({ seq, day: days[i]!, state: i === attempts && today >= days[i]! ? 'due' : 'upcoming' });
  }
  return marks;
}

const SEQ_TAB: Partial<Record<ConfirmationState, 'first' | 'second' | 'third'>> = {
  'due-seq-1': 'first',
  'due-seq-2': 'second',
  'due-seq-3': 'third',
};
const SEQ_LANE: Record<'first' | 'second' | 'third', ConfirmationLane> = {
  first: 'seq-1',
  second: 'seq-2',
  third: 'seq-3',
};

/**
 * THE Confirmations worklist (one screen, tv-2na.11.2), bucketed on the
 * @atink/domain `confirmationStateOf` contract. Pure: plain records in, rows out.
 *
 * - Scope: solid, live bookings only (held ghosts / pending deposit / overbookable
 *   are not in the cadence), plus a call-confirmed hold that still needs a deposit.
 * - Window: stops at the day before the appointment. Today's arrivals (confirmed
 *   or not) are the Floor's.
 * - first/second/third: that sequence's call is due (rolled forward until made).
 * - reds:      escalated (3 unanswered sequences, or an Ops flag).
 * - confirmed: call-confirmed, or confirmed at booking (short-notice / gap-fill,
 *   which skip the cadence and are never overdue). Listed from the booking's
 *   seq 1 day (its cadence window) through the day before.
 */
export function buildConfirmationWorklist(args: {
  nowIso: string;
  bookings: Record<string, Booking>;
  confirmationCalls: Record<string, ConfirmationCall>;
  studioIds: StudioId[];
}): ConfirmationWorklist {
  const { nowIso, bookings, confirmationCalls, studioIds } = args;
  const inScope = (b: Booking) => studioIds.length === 0 || studioIds.includes(b.studioId);
  const callByBooking = new Map<string, ConfirmationCall>();
  for (const c of Object.values(confirmationCalls)) callByBooking.set(c.bookingId, c);

  const tabs: Record<WorklistTab, ConfirmationRow[]> = { first: [], second: [], third: [], reds: [], confirmed: [] };
  const today = localKey(nowIso);

  for (const b of Object.values(bookings)) {
    if (!inScope(b)) continue;
    const appointmentDay = localKey(b.start);
    if (appointmentDay <= today) continue; // the day before is the last day here
    const holdNeedsDeposit = needsDeposit(b);
    if (!isInCadenceScope(b) && !holdNeedsDeposit) continue; // held ghosts are not in the cadence

    const state = confirmationStateOf(b, nowIso);
    const seqTab = SEQ_TAB[state];
    const tab: WorklistTab | null = seqTab
      ? seqTab
      : state === 'escalated'
        ? 'reds'
        : state === 'confirmed' || state === 'confirmed-at-booking'
          ? 'confirmed'
          : null;
    if (!tab) continue; // waiting: the next sequence day is not reached yet
    // A booking confirmed early joins Confirmed once its cadence window opens
    // (its seq 1 day), then stays through the day before.
    if (tab === 'confirmed' && today < cadenceSchedule(b.start).seq1) continue;

    const call = callByBooking.get(b.id);
    let overdue = tab === 'reds';
    if (seqTab) {
      const s = cadenceSchedule(b.start);
      const dueDay = { first: s.seq1, second: s.seq2, third: s.seq3 }[seqTab];
      const staleCall = call !== undefined && call.outcome === undefined && localKey(call.scheduledFor) < today;
      overdue = today > dueDay || staleCall;
    }
    const deadline = confirmationDeadlineState(b, nowIso);
    tabs[tab].push({
      booking: b,
      state,
      lane: seqTab ? SEQ_LANE[seqTab] : null,
      atRisk: tab !== 'confirmed' && (deadline === 'refillable' || deadline === 'deadline-approaching'),
      overdue,
      needsDeposit: holdNeedsDeposit,
      appointmentDay,
      touches: touchTimeline(b, state, nowIso),
      call,
    });
  }

  // Oldest-first within each tab: overdue (waited past its day) at the top, then
  // soonest appointment next.
  const byOverdueThenStart = (a: ConfirmationRow, z: ConfirmationRow) => {
    if (a.overdue !== z.overdue) return a.overdue ? -1 : 1;
    return a.booking.start.localeCompare(z.booking.start);
  };
  for (const arr of Object.values(tabs)) arr.sort(byOverdueThenStart);

  return withCounts(tabs);
}

function withCounts(tabs: Record<WorklistTab, ConfirmationRow[]>): ConfirmationWorklist {
  return {
    ...tabs,
    counts: {
      first: tabs.first.length,
      second: tabs.second.length,
      third: tabs.third.length,
      reds: tabs.reds.length,
      confirmed: tabs.confirmed.length,
    },
  };
}

// ---------------------------------------------------------------------------
// Appointment day pairs (D06-04, option 3). A pair is the two appointment days
// that share one cadence (Mon+Tue, Wed+Thu, Fri+Sat): the strip on the screen
// shows each upcoming pair's progress and filters the tabs + list to one pair.
// The pairing and the sequence days come from `cadenceSchedule` (@atink/domain),
// never a second rule.
// ---------------------------------------------------------------------------

const WORKLIST_TABS: readonly WorklistTab[] = ['first', 'second', 'third', 'reds', 'confirmed'];

/** Day key (YYYY-MM-DD) plus n calendar days. */
function plusDayKey(key: string, n: number): string {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

/** "Mon 24" for a day key, the same in every browser zone. */
export const dayLabel = (key: string) => format(parseISO(key), 'EEE d');

export interface PairIdentity {
  /** The pair's first appointment day (studio day key): unique per pair. */
  key: string;
  days: [string, string];
  /** "Mon 24 + Tue 25" */
  label: string;
  /** "Mon 24+Tue 25", for per-pair splits */
  shortLabel: string;
  schedule: CadenceSchedule;
}

/**
 * The appointment pair a booking start falls in, with its cadence. A Sunday
 * appointment is its own entry ("Sun 10"): `cadenceSchedule` runs it on the
 * previous week's Mon+Tue sequence days, and a "Mon 4 + Tue 5" label would
 * name days already gone.
 */
export function pairOf(startIso: string): PairIdentity {
  const schedule = cadenceSchedule(startIso);
  const day = studioDayKey(startIso);
  const dow = new Date(`${day}T00:00:00Z`).getUTCDay();
  if (dow === 0) {
    return { key: day, days: [day, day], label: dayLabel(day), shortLabel: dayLabel(day), schedule };
  }
  const monday = plusDayKey(day, -((dow + 6) % 7)); // same week anchor as cadenceSchedule
  const offset = { 'mon-tue': 0, 'wed-thu': 2, 'fri-sat': 4 }[schedule.pair];
  const first = plusDayKey(monday, offset);
  const second = plusDayKey(first, 1);
  return {
    key: first,
    days: [first, second],
    label: `${dayLabel(first)} + ${dayLabel(second)}`,
    shortLabel: `${dayLabel(first)}+${dayLabel(second)}`,
    schedule,
  };
}

export interface AppointmentPair extends PairIdentity {
  /** Today is on or after the pair's Sequence 1 day. */
  started: boolean;
  /** Live, in-cadence bookings in the pair still ahead (after today). */
  total: number;
  confirmed: number;
  /** In a Sequence tab: to be worked today (rolled-forward rows included). */
  due: number;
  /** Of `due`, the rows past their own sequence day (the row's "Waiting ·"). */
  waiting: number;
  /** Escalated: on the Ops worklist. */
  withOps: number;
  /** Not confirmed and not with Ops: the amber part of the progress bar. */
  toConfirm: number;
}

/**
 * The upcoming appointment pairs for the strip: every pair whose sequences have
 * started and still has a booking ahead, then the next pair not started yet.
 * Totals use the worklist's own scope (live, in-cadence, after today); due /
 * waiting / with Ops are read off the worklist rows, so they always match the tabs.
 */
export function buildAppointmentPairs(args: {
  nowIso: string;
  bookings: Record<string, Booking>;
  studioIds: StudioId[];
  worklist: ConfirmationWorklist;
}): AppointmentPair[] {
  const { nowIso, bookings, studioIds, worklist } = args;
  const today = localKey(nowIso);
  const byKey = new Map<string, AppointmentPair>();
  const pairFor = (b: Booking): AppointmentPair => {
    const id = pairOf(b.start);
    let p = byKey.get(id.key);
    if (!p) {
      p = { ...id, started: today >= id.schedule.seq1, total: 0, confirmed: 0, due: 0, waiting: 0, withOps: 0, toConfirm: 0 };
      byKey.set(id.key, p);
    }
    return p;
  };

  for (const b of Object.values(bookings)) {
    if (studioIds.length > 0 && !studioIds.includes(b.studioId)) continue;
    if (localKey(b.start) <= today) continue;
    if (!isInCadenceScope(b) && !needsDeposit(b)) continue;
    const state = confirmationStateOf(b, nowIso);
    if (state === 'not-in-cadence') continue;
    const p = pairFor(b);
    p.total += 1;
    if (state === 'confirmed' || state === 'confirmed-at-booking') p.confirmed += 1;
  }
  for (const tab of ['first', 'second', 'third'] as const) {
    for (const row of worklist[tab]) {
      const p = byKey.get(pairOf(row.booking.start).key);
      if (!p) continue;
      p.due += 1;
      if (row.overdue) p.waiting += 1;
    }
  }
  for (const row of worklist.reds) {
    const p = byKey.get(pairOf(row.booking.start).key);
    if (p) p.withOps += 1;
  }

  const all = [...byKey.values()].sort((a, z) => a.key.localeCompare(z.key));
  for (const p of all) p.toConfirm = Math.max(0, p.total - p.confirmed - p.withOps);
  const started = all.filter((p) => p.started);
  const next = all.find((p) => !p.started);
  return next ? [...started, next] : started;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The pair card's one line: "12 of 18 confirmed · 5 due (1 waiting)", or when its sequences start. */
export function pairSummary(p: AppointmentPair): string {
  if (!p.started) {
    const head = p.confirmed > 0 ? `${p.confirmed} of ${p.total} confirmed` : plural(p.total, 'booking', 'bookings');
    return `${head} · sequences start ${dayLabel(p.schedule.seq1)}`;
  }
  const parts = [`${p.confirmed} of ${p.total} confirmed`];
  // "waiting" is a subset of "due" (rows past their own sequence day), so it is bracketed.
  if (p.due) parts.push(`${p.due} due${p.waiting ? ` (${p.waiting} waiting)` : ''}`);
  if (p.withOps) parts.push(`${p.withOps} with Ops`);
  return parts.join(' · ');
}

/** "Sequences for this pair: Thu 13 · Fri 14 · Sat 15 (today). Anyone still unconfirmed goes to Ops on Mon 17." */
export function pairScheduleLine(p: PairIdentity, todayKey: string): string {
  const s = p.schedule;
  const days = [s.seq1, s.seq2, s.seq3].map((d) => `${dayLabel(d)}${d === todayKey ? ' (today)' : ''}`).join(' · ');
  const ops =
    s.decision >= todayKey
      ? `Anyone still unconfirmed goes to Ops on ${dayLabel(s.decision)}${s.decision === todayKey ? ' (today)' : ''}.`
      : `Anyone unconfirmed went to Ops on ${dayLabel(s.decision)}.`;
  return `Sequences for this pair: ${days}. ${ops}`;
}

/** The worklist narrowed to one appointment pair (null = all days), counts recomputed. */
export function filterWorklistByPair(w: ConfirmationWorklist, pairKey: string | null): ConfirmationWorklist {
  if (pairKey === null) return w;
  const keep = (rows: ConfirmationRow[]) => rows.filter((r) => pairOf(r.booking.start).key === pairKey);
  return withCounts({
    first: keep(w.first),
    second: keep(w.second),
    third: keep(w.third),
    reds: keep(w.reds),
    confirmed: keep(w.confirmed),
  });
}

export interface TabFigures {
  count: number;
  /** "4 due today", "2 due today (1 waiting) · last before Ops", "With Ops · decide Mon 11", "17 ahead". */
  dueLine: string;
  /** The pair(s) the tab covers: one label, or "2 Mon 11+Tue 12 · 1 Wed 13+Thu 14". */
  pairsLine: string;
}

/** Each tab card's figures for the (possibly pair-filtered) worklist. */
export function tabFigures(args: {
  worklist: ConfirmationWorklist;
  pairs: AppointmentPair[];
  selected: string | null;
  todayKey: string;
}): Record<WorklistTab, TabFigures> {
  const { worklist, pairs, selected, todayKey } = args;
  const pairsLine = (rows: ConfirmationRow[]): string => {
    const counts = new Map<string, { id: PairIdentity; n: number }>();
    for (const r of rows) {
      const id = pairOf(r.booking.start);
      const c = counts.get(id.key) ?? { id, n: 0 };
      c.n += 1;
      counts.set(id.key, c);
    }
    const list = [...counts.values()].sort((a, z) => a.id.key.localeCompare(z.id.key));
    if (list.length === 0) return '—';
    if (list.length === 1) return list[0]!.id.label;
    return list.map((c) => `${c.n} ${c.id.shortLabel}`).join(' · ');
  };
  const seqLine = (rows: ConfirmationRow[], last: boolean): string => {
    if (rows.length === 0) return 'none due';
    // Every row in a Sequence tab is to be worked today; "waiting" says how many
    // of them are past their own sequence day. The header's "due today" is the sum.
    const waiting = rows.filter((r) => r.overdue).length;
    return `${rows.length} due today${waiting ? ` (${waiting} waiting)` : ''}${last ? ' · last before Ops' : ''}`;
  };
  const redsLine = (rows: ConfirmationRow[]): string => {
    if (rows.length === 0) return 'None with Ops';
    const decision = rows.map((r) => cadenceSchedule(r.booking.start).decision).sort()[0]!;
    return decision >= todayKey ? `With Ops · decide ${dayLabel(decision)}` : `With Ops · decision was due ${dayLabel(decision)}`;
  };
  const selectedPair = selected ? pairs.find((p) => p.key === selected) : undefined;
  const confirmedLine = (rows: ConfirmationRow[]): string => {
    if (!selectedPair) return `${rows.length} ahead`;
    // A pair whose sequences haven't started: its early confirmations join the
    // list on its Sequence 1 day, but the figure matches the strip now.
    if (!selectedPair.started) {
      return `${selectedPair.confirmed} of ${selectedPair.total} · listed from ${dayLabel(selectedPair.schedule.seq1)}`;
    }
    return `${rows.length} of ${selectedPair.total}`;
  };

  const out = {} as Record<WorklistTab, TabFigures>;
  for (const tab of WORKLIST_TABS) {
    const rows = worklist[tab];
    const dueLine =
      tab === 'reds' ? redsLine(rows) : tab === 'confirmed' ? confirmedLine(rows) : seqLine(rows, tab === 'third');
    out[tab] = { count: rows.length, dueLine, pairsLine: pairsLine(rows) };
  }
  return out;
}

/** The screen's "due today" figure, and the LeftRail badge: calls due across the three sequences. */
export function confirmationsDueCount(w: Pick<ConfirmationWorklist, 'counts'>): number {
  return w.counts.first + w.counts.second + w.counts.third;
}
